/**
 * Translating a SAPIENT alert into a Flow-F notification (ADR-029 §5 / §6.1).
 *
 * Pure functions only — the hook that actually raises them is
 * :func:`alerts/use-alert-notifications`. Keeping the copy and the
 * threshold logic here means both are unit-testable without a provider,
 * and the operator-facing wording lives in one place.
 *
 * # The severity map is NOT re-derived here
 *
 * :func:`alerts/alert-display.describeAlertPriority` already encodes
 * ADR-029 §5's table, including the ``flowF`` surface each priority raises.
 * This module reads its ``severity`` and lets the notification host's own
 * routing (:func:`notifications/notification-timing.notificationSlot` +
 * ``notificationDurationMs``) place it. Those two agree by construction —
 * ``info``/``warning`` → auto-dismissing toast, ``error`` → sticky toast,
 * ``critical`` → banner — and
 * :file:`alert-notification.test.ts` asserts the agreement for every
 * priority, so a change to either side that breaks the correspondence
 * fails there rather than silently mis-surfacing an alert.
 *
 * Cross-references:
 *   - ADR-029 §5 (severity map) / §6.1 (threshold) / §6.2 (Dismiss copy) /
 *     §7 (system-wide dismissal scope) / §10 (flood cap).
 *   - REQ-UI-062; storyboard Flow F Frame 1 + Frame 3 (SAPIENT variant).
 *   - feature-specs/alert-notification-surface/feature-spec.md §0.1 D1.
 */
import { alertTypeLabel, describeAlertPriority, formatConfidence } from "@/alerts/alert-display";
import type { AlertRecord } from "@/alerts/alerts-store";
import type { ProfileDocument } from "@/auth/session-types";
import type { NotificationAction, NotificationInput } from "@/notifications/notification-types";

/**
 * The operator's SAPIENT-alert notification threshold (ADR-029 §6.1).
 *
 *   - ``off`` — no alert notification at all, **including CRITICAL**
 *     (2026-08-31; the bypass was removed).
 *   - ``high_plus`` — HIGH and above, which includes CRITICAL.
 *   - ``all`` — every alert raises its mapped surface.
 *
 * **Every setting gates every severity** since 2026-08-31 — see
 * :func:`shouldNotifyForAlert` for why that changed and what it costs.
 *
 * Derived from the generated BFF schema rather than restated, so the UI's
 * accepted values cannot drift from the persisted contract: the Pydantic model
 * has ``extra="forbid"`` and a strict Literal, so a value this type allowed but
 * the BFF did not would fail validation on save, not at compile time.
 */
export type AlertNotificationThreshold = NonNullable<
  ProfileDocument["notifications"]
>["sapient_alert_threshold"];

/**
 * The ``notifications`` section version the BFF treats as current. Every write
 * of the section must carry it (REQ-UI-130; storyboard Flow F, F.12).
 *
 * The stamp is not bookkeeping. The BFF's v1 → v2 migration (the 2026-08-31
 * default flip) rewrites a v1 ``all`` to ``off`` whenever the section is
 * validated, and a save validates. The server cannot tell a v1 section written
 * a moment ago from one stored before the flip. A writer stamping an older
 * version therefore has an explicit "All" persisted as "Off", and "Off" also
 * silences CRITICAL. That happened: the preferences menu stamped ``1`` from #994
 * until 2026-09-21. The UI applies the choice optimistically, so it held for
 * the session and reverted at the next load.
 *
 * Not derivable at compile time: the generated type is ``number``, and the
 * value lives in the schema's ``default``, which openapi-typescript emits as a
 * JSDoc tag rather than a type. :file:`alert-notification.test.ts` pins this
 * constant to that ``@default`` in ``api-types.gen.ts`` (which
 * ``ui-codegen-verify`` holds to ``openapi.yaml``). A BFF bump that is not
 * matched here fails the UI suite.
 */
export const NOTIFICATION_SCHEMA_VERSION: NonNullable<
  ProfileDocument["notifications"]
>["notification_schema_version"] = 2;

/**
 * The shipped default: ``off`` (architect direction 2026-08-31).
 *
 * The toast layer is painted **over** the workspace (``fixed bottom-6 right-6
 * z-50``), so a busy alert stream sits on top of whatever panel content is
 * underneath it. The near-term mitigation is to stop raising alert
 * notifications unless an operator asks for them.
 *
 * ⚠ **Interim posture, not a settled end state.** The notification surface is
 * to be reworked as its own feature; that work should re-decide this default
 * rather than inherit it.
 *
 * The default has now moved twice, which is worth knowing before changing it a
 * third time: §6.1 as written (2026-05-25) said ``high_plus``, under which a
 * MEDIUM alert raised **nothing** — contradicting Flow F Frame 1, acceptance
 * F.9 and REQ-UI-062's Notes, all of which describe MEDIUM → a warning toast.
 * That was corrected to ``all`` on 2026-07-26 (feature-spec §0.1 D1). The
 * 2026-08-31 flip to ``off`` is not a return to the §6.1 wording — it is a
 * deliberate quieting of the whole surface, paired with the removal of the
 * CRITICAL bypass in :func:`shouldNotifyForAlert`.
 */
export const DEFAULT_ALERT_NOTIFICATION_THRESHOLD: AlertNotificationThreshold = "off";

/**
 * Whether an alert of this priority should raise a notification under the
 * operator's ``threshold``.
 *
 * ⚠ **``off`` is total, including CRITICAL** (architect direction 2026-08-31).
 * Until then CRITICAL bypassed the threshold under every setting, per ADR-029
 * §6.1 (*"sticky banners for CRITICAL alerts are always shown"*) and Flow F
 * (*"not preference-controlled (always on, can't disable)"*). Removing that
 * bypass is a **deliberate reversal of a fail-safe** (CLAUDE.md §8.1 would
 * otherwise argue for keeping it) and is flagged for accreditation review: a
 * deployment on the shipped default raises no alert notification of any
 * severity until an operator opts in. The surface presenting the control
 * states this (REQ-UI-130), because an operator choosing ``off`` must not
 * discover the scope of that choice during an incident.
 *
 * # ``high_plus`` must include CRITICAL, and the bypass used to hide that
 *
 * Priority 4 (Critical) maps to ``severity: "critical"``, not ``"error"``, so
 * the ``high_plus`` arm's ``severity === "error"`` test is **false** for a
 * CRITICAL alert. While the bypass existed it returned early and the arm was
 * never reached; deleting the bypass without widening the arm would have
 * silenced CRITICAL under *"High and above"* — a setting whose whole meaning
 * is that it includes everything above HIGH. Hence the explicit two-severity
 * test below, and the per-priority-per-threshold test matrix over this
 * function rather than aggregate assertions.
 */
export function shouldNotifyForAlert(
  priority: number,
  threshold: AlertNotificationThreshold = DEFAULT_ALERT_NOTIFICATION_THRESHOLD,
): boolean {
  const { severity } = describeAlertPriority(priority);
  switch (threshold) {
    case "off":
      return false;
    case "high_plus":
      // "High and above" — HIGH (error) *and* CRITICAL. See the note above.
      return severity === "error" || severity === "critical";
    case "all":
      return true;
    default:
      // An unrecognised threshold is a MALFUNCTION, not a choice, and the two
      // are not interchangeable: a deliberate ``off`` is an operator accepting
      // silence, whereas this is us being unable to read what they chose. So
      // this arm surfaces rather than suppresses — non-notification is the
      // loss condition here, the same reasoning that makes the ingress filter
      // fail ALLOW-ALL (security.md, "non-consumption is the harm").
      //
      // Without this arm the switch falls off its end and returns ``undefined``,
      // which every call site reads as falsy — so an unreadable preference
      // silences *every* severity with no error anywhere. That is reachable:
      // the UI bundle and the BFF deploy separately behind their own load
      // balancers (ADR-020, ADR-027), so a rolling deploy can serve an older
      // bundle a threshold literal it predates. TypeScript cannot catch it —
      // it reasons over the declared union, not the JSON the BFF sends.
      return true;
  }
}

/** The dedupe key for an alert's notification. */
export function alertNotificationKey(alertId: string): string {
  return `alert:${alertId}`;
}

/**
 * The notification body line for an alert — source Producer, alert type,
 * and confidence, mirroring the Alerts-panel row's information and Flow F
 * Frame 3's "BD-RDR-01 · confidence 0.84 · …" shape.
 */
export function alertNotificationBody(alert: AlertRecord): string {
  return [
    alert.producer_id,
    alertTypeLabel(alert.alert_type),
    `confidence ${formatConfidence(alert.confidence)}`,
  ].join(" · ");
}

/**
 * Build the :type:`NotificationInput` for an arriving alert.
 *
 * The title prefers the source's own ``description`` (the operator-readable
 * field BSI provides) and falls back to the alert-type label when it is
 * absent or blank — a null description is a normal state, not an error, so
 * the notification must still say something legible.
 *
 * ``actions`` are supplied by the caller because they close over React
 * context (the selection bus, the dismiss mutation). Pass ``onView`` /
 * ``onDismiss`` as ``undefined`` to omit that affordance — ``onDismiss``
 * is omitted for roles without the ``alerts.dismiss`` capability, matching
 * the Alerts panel's fail-safe of hiding a control the BFF would reject
 * (ADR-029 §9; ``ALERT_DISMISSER_ROLES``).
 */
export function buildAlertNotification(
  alert: AlertRecord,
  actions: {
    readonly onView?: () => void;
    readonly onDismiss?: () => void;
  } = {},
): NotificationInput {
  const { severity } = describeAlertPriority(alert.priority);
  const description = alert.description?.trim();
  const built: NotificationAction[] = [];
  if (actions.onView !== undefined) {
    built.push({ label: "View →", onSelect: actions.onView });
  }
  if (actions.onDismiss !== undefined) {
    // systemWide: dismissal clears the alert for EVERY operator on the
    // network (ADR-029 §7), so it is a labelled action and never the ×.
    built.push({ label: "Dismiss", onSelect: actions.onDismiss, systemWide: true });
  }
  return {
    severity,
    title:
      description !== undefined && description !== ""
        ? description
        : `${alertTypeLabel(alert.alert_type)} alert`,
    body: alertNotificationBody(alert),
    // One live notification per alert: an alert re-delivered by the REST
    // poll or a burst from one Producer collapses instead of stacking
    // (ADR-029 §10 at the UI end).
    dedupeKey: alertNotificationKey(alert.alert_id),
    source: "alert",
    ...(built.length > 0 ? { actions: built } : {}),
  };
}
